/**
 * The one external maps destination — Task 11.3, requirement 6.4.
 *
 * ## Why this lives in the contracts package
 *
 * A directions link needs a provider hostname, and the repository's frontend scan rejects any absolute
 * origin written into `apps/**` source. That rule exists because the site being replaced had one API
 * hostname copied into five files, so a hostname in an app is treated as a defect regardless of what it
 * points at. The established way to sanction one is the way the content-image origins are sanctioned:
 * declared once here, imported by whoever renders it, reviewed as a contract change rather than as a
 * component detail. So the apps still contain no hostname literal, and there is exactly one line to
 * change if the provider ever changes.
 *
 * ## Why Google Maps
 *
 * Its documented `api=1` directions URL is the one that behaves the same everywhere: it opens the
 * native app on Android and iOS and the web map on a desktop, from a plain link, with no SDK and no
 * platform sniffing. An Apple Maps URL works only on Apple platforms, and a `geo:` URI is unhandled on
 * most desktops. The alternative — asking a visitor which provider they use — is a question the page
 * does not need to ask to draw a link.
 *
 * ## What this deliberately is not
 *
 * Not a request. This builds a URL for an anchor's `href`, so the provider learns a destination only if
 * a visitor chooses to follow it, and it learns nothing at all otherwise. The visitor's own position is
 * never part of it: the URL carries the destination only, and the provider asks for the starting point
 * itself if the visitor wants it. That is what keeps "the position is never sent anywhere" true even on
 * the one code path that exists to lead somewhere else.
 */
/** The provider origin. The single sanctioned external maps hostname in the system. */
export const directionsProviderOrigin = 'https://www.google.com';
/** The provider's stable, documented directions entry point. */
export const directionsProviderPath = '/maps/dir/';
/**
 * `latitude,longitude`, which is the order this provider's `destination` parameter takes — and the
 * reverse of the order the map library's `setLngLat` takes. Both orders exist in this system on purpose;
 * each is written once, next to the thing that defines it.
 *
 * `URLSearchParams` rather than string concatenation, so the comma and any future parameter are encoded
 * by the platform. A test can then read the query back through the same API instead of matching a
 * hand-built string.
 */
export const buildDirectionsUrl = ({ latitude, longitude }) => {
    const url = new URL(directionsProviderPath, directionsProviderOrigin);
    url.search = new URLSearchParams({
        api: '1',
        destination: `${String(latitude)},${String(longitude)}`,
    }).toString();
    return url.href;
};
//# sourceMappingURL=directions.js.map